import Icon from '@components/atoms/icon';
import {
  BodyRegularBold,
  BodySmallRegular
} from '@components/atoms/typography';
import {
  collectSimulatedProvenances,
  countSimulatedPicks
} from '@lib/live/team/aggregation';
import { useTeamLive } from '@lib/live/team/context';

/**
 * How much of what the pitch is showing came from a simulated fixture.
 *
 * `attached` covers a simulated fixture we hold state for that no pick has
 * resolved against yet. It is transient during subscription, but it is also
 * the honest answer for a squad that features nobody in the simulated match:
 * that fixture still feeds the pitch's availability and fixture-priority
 * logic, so saying nothing would be wrong.
 */
export type SimulatedExtent = 'none' | 'attached' | 'partial' | 'all';

export const resolveSimulatedExtent = ({
  hasSimulatedFixture,
  simulated,
  withFixture
}: {
  hasSimulatedFixture: boolean;
  simulated: number;
  withFixture: number;
}): SimulatedExtent => {
  if (!hasSimulatedFixture) return 'none';
  if (simulated === 0) return 'attached';
  return simulated === withFixture ? 'all' : 'partial';
};

const describeExtent = (
  extent: SimulatedExtent,
  { simulated, withFixture }: { simulated: number; withFixture: number }
): string => {
  if (extent === 'all') {
    return 'Every fixture behind this pitch is simulated, so the scores, points and events on it are fabricated.';
  }
  if (extent === 'partial') {
    // Naming the split matters more than naming the fact. A partially
    // simulated total is more misleading than a fully simulated one, because
    // the real picks lend it credibility the fabricated ones do not deserve.
    return `${simulated} of your ${withFixture} picks with a fixture are showing simulated data, marked "Sim" on the pitch. The running total mixes fabricated points with real ones.`;
  }
  return 'A simulated fixture is attached to this pitch. Nothing in your squad is scoring from it yet.';
};

/**
 * Persistent, non-dismissible marking for a pitch backed by a simulated match.
 *
 * Reads match state from the team-live context rather than taking props, so a
 * surface that starts rendering `MatchState` later inherits this by dropping
 * the component in, and cannot silently omit it by forgetting to thread a
 * flag through. It renders nothing at all for a real match, so it is safe to
 * mount unconditionally.
 *
 * Deliberately not gated behind any developer setting. The case it exists for
 * is somebody opening a staging build with no developer tooling enabled and
 * taking fabricated points at face value.
 */
export const SimulatedMatchNotice = () => {
  const { picks, matchStates } = useTeamLive();

  const provenances = collectSimulatedProvenances(matchStates);
  const counts = countSimulatedPicks(picks, matchStates);
  const extent = resolveSimulatedExtent({
    hasSimulatedFixture: provenances.length > 0,
    ...counts
  });

  if (extent === 'none') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-cy="simulated-match-notice"
      data-cy-extent={extent}
      className="mt-6 flex items-start gap-3 rounded-xl border-2 border-secondary-600 bg-secondary-50 px-4 py-3"
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
        <Icon name="Warning" className="h-5 w-5" />
      </span>
      <div className="flex flex-col gap-1">
        <BodyRegularBold textColor="text-secondary-700">
          Simulated match data
        </BodyRegularBold>
        <BodySmallRegular textColor="text-neutral-700">
          {describeExtent(extent, counts)}
        </BodySmallRegular>
        <BodySmallRegular textColor="text-neutral-600">
          Source: {provenances.join(', ')}
        </BodySmallRegular>
      </div>
    </div>
  );
};
